import { useCallback, useState } from 'react';
import { FlattenedLogItem } from './useFlattenedLogs';

export interface UseLogPopoverProps {
  logItems: FlattenedLogItem[];
}

export interface UseLogPopoverReturn {
  popoverLogObject: FlattenedLogItem | null;
  popoverAnchorEl: HTMLElement | null;
  isOpen: boolean;
  handleLogLineClick: (event: React.MouseEvent<HTMLDivElement>, logItem: FlattenedLogItem) => void;
  handleLogLineKeyDown: (
    event: React.KeyboardEvent<HTMLDivElement>,
    logItem: FlattenedLogItem,
  ) => void;
  handlePopoverClose: () => void;
  getNavigationHandlers: (currentLog: FlattenedLogItem) => {
    onClickPreviousLog?: () => void;
    onClickNextLog?: () => void;
  };
}

type PopoverAnchorEl = HTMLElement | null;
type PopoverState = FlattenedLogItem | null;

/**
 * Hook to manage popover state and interactions for log lines
 *
 * @param logItems - Array of flattened log items for navigation functionality
 * @returns Popover state, handlers, and navigation functions
 */
export function useLogPopover(props: UseLogPopoverProps): UseLogPopoverReturn {
  const { logItems = [] } = props;
  const [popoverLogObject, setPopoverLogObject] = useState<PopoverState>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<PopoverAnchorEl>(null);

  const isOpen = Boolean(popoverAnchorEl);

  const handleLogLineClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, logItem: FlattenedLogItem) => {
      setPopoverAnchorEl(event.currentTarget);
      setPopoverLogObject(logItem);
    },
    [],
  );

  const handleLogLineKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, logItem: FlattenedLogItem) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setPopoverAnchorEl(event.currentTarget as HTMLElement);
        setPopoverLogObject(logItem);
      }
    },
    [],
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchorEl(null);
    setPopoverLogObject(null);
  }, []);

  const getNavigationHandlers = useCallback(
    (currentLog: FlattenedLogItem) => {
      /**
       * @Why?
       * If at first log, return undefined for previous log
       * If at last log, return undefined for next log
       * It makes the navigation buttons disabled if they are undefined
       */
      const handlers: {
        onClickPreviousLog?: () => void;
        onClickNextLog?: () => void;
      } = {};

      if (typeof currentLog.logIndex !== 'number') return handlers;

      const prevIndex = currentLog.logIndex - 1;
      if (prevIndex >= 0 && prevIndex < logItems.length) {
        handlers.onClickPreviousLog = () => {
          setPopoverLogObject(logItems[prevIndex]);
        };
      }

      const nextIndex = currentLog.logIndex + 1;
      if (nextIndex >= 0 && nextIndex < logItems.length) {
        handlers.onClickNextLog = () => {
          setPopoverLogObject(logItems[nextIndex]);
        };
      }

      return handlers;
    },
    [logItems],
  );

  return {
    popoverLogObject,
    popoverAnchorEl,
    isOpen,
    handleLogLineClick,
    handleLogLineKeyDown,
    handlePopoverClose,
    getNavigationHandlers,
  };
}
